import { ReactNode, useRef } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import { Box, GlobalStyles } from '@mui/material';
import { SnackbarProvider, SnackbarKey } from 'notistack';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoNotDisturbOnIcon from '@mui/icons-material/DoNotDisturbOn';
import ErrorIcon from '@mui/icons-material/Error';
import CloseIcon from '@mui/icons-material/Close';

import { IconButtonAnimate } from './animate/IconButtonAnimate';

const SnackbarStyles = () => {
  const theme = useTheme();

  const shadowTransparency = alpha('#919EAB', 0.16);

  return (
    <GlobalStyles
      styles={{
        '#root': {
          '& .SnackbarContent-root': {
            width: '100%',
            padding: theme.spacing(1),
            margin: theme.spacing(0.25, 0),
            boxShadow: `0 8px 16px 0 ${shadowTransparency}`,
            borderRadius: theme.shape.borderRadius,
            color: '#FFFFFF',
            backgroundColor: '#161C24',
            '&.SnackbarItem-variantSuccess, &.SnackbarItem-variantError, &.SnackbarItem-variantWarning, &.SnackbarItem-variantInfo':
              {
                color: '#000000DE',
                backgroundColor: 'white',
              },
            [theme.breakpoints.up('md')]: {
              minWidth: 240,
            },
          },
          '& .SnackbarItem-message': {
            fontFamily: 'Roboto',
            padding: '0 !important',
            fontWeight: 600,
          },
          '& .SnackbarItem-action': {
            marginRight: 0,
            color: 'theme.palette.action.active',
            '& svg': { width: 20, height: 20 },
          },
        },
      }}
    />
  );
};

interface SnackbarIconProps {
  icon: ReactNode;
  color: string;
}

const SnackbarIcon = ({ icon, color }: SnackbarIconProps) => (
  <Box
    component="span"
    sx={{
      mr: 1.5,
      width: 40,
      height: 40,
      display: 'flex',
      borderRadius: 1.5,
      alignItems: 'center',
      justifyContent: 'center',
      color: `${color}.main`,
      bgcolor: (theme) => alpha(color, 0.16),
    }}
  >
    {icon}
  </Box>
);

interface NotistackProviderProps {
  children: ReactNode;
}

export const NotistackProvider = ({ children }: NotistackProviderProps) => {
  const notistackRef = useRef<any>(null);

  const onClose = (key: SnackbarKey) => () => {
    notistackRef.current.closeSnackbar(key);
  };

  return (
    <>
      <SnackbarStyles />

      <SnackbarProvider
        ref={notistackRef}
        dense
        maxSnack={5}
        preventDuplicate
        autoHideDuration={3000}
        TransitionComponent={undefined}
        variant="success" // Set default variant
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        iconVariant={{
          info: <SnackbarIcon icon={<InfoIcon sx={{ fill: 'blue' }} />} color="#1890FF" />,
          success: (
            <SnackbarIcon icon={<CheckCircleIcon sx={{ fill: 'green' }} />} color="#54D62C" />
          ),
          warning: (
            <SnackbarIcon icon={<DoNotDisturbOnIcon sx={{ fill: 'orange' }} />} color="#FFC107" />
          ),
          error: <SnackbarIcon icon={<ErrorIcon sx={{ fill: 'red' }} />} color="#FF4842" />,
        }}
        // With close as default
        action={(key) => (
          <IconButtonAnimate size="small" onClick={onClose(key)} sx={{ p: 0.5 }}>
            <CloseIcon sx={{ width: '18px', height: '18px' }} />
          </IconButtonAnimate>
        )}
      >
        {children}
      </SnackbarProvider>
    </>
  );
};
